import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { storage } from "../config/firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import imagensPublicasService from "../services/imagensPublicasService";
import { useAuth } from "../contexts/AuthContext";
import Icon from "./Icon";
import "./AreaVisitante.css";
import logoExtendida from "../assets/icesextendida2.png";
import instagram from "../assets/instagram.png";
import whatsapp from "../assets/whatsapp.png";

// Componente para partículas flutuantes decorativas
const FloatingParticles = () => (
  <div className="floating-particles" aria-hidden="true">
    {[...Array(6)].map((_, i) => (
      <span key={i} className="particle" style={{ '--delay': `${i * 1.2}s` }} />
    ))}
  </div>
);

const AreaVisitante = () => {
  const navigate = useNavigate();
  const { currentUser, churchUser } = useAuth();
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedImageUrl, setExpandedImageUrl] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [headerHidden, setHeaderHidden] = useState(false);
  const [expandedConfissao, setExpandedConfissao] = useState(null);
  const observerRef = useRef(null);
  const carouselIntervalRef = useRef(null);
  const lastScrollY = useRef(0);
  const visitorAreaRef = useRef(null);
  const headerRef = useRef(null);

  // Verifica se o usuário está logado (Header.jsx já será exibido)
  const isLoggedIn = currentUser && churchUser;

  // Função para reiniciar o timer do carrossel
  const resetCarouselTimer = useCallback(() => {
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
    }
    if (images.length > 1) {
      carouselIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 7000);
    }
  }, [images.length]);

  // Function to load images from Firebase Storage
  const loadImages = useCallback(async () => {
    try {
      const imagesRef = ref(storage, "imagens_publicas");
      const result = await listAll(imagesRef);

      // Buscar metadados do Firestore
      const metadados = await imagensPublicasService.listarMetadados();

      // Criar set com nomes de arquivos que existem no Storage
      const arquivosExistentes = new Set(result.items.map((item) => item.name));

      // Filtrar metadados apenas para arquivos que existem no Storage
      const metadadosValidos = metadados.filter((m) =>
        arquivosExistentes.has(m.nomeArquivo)
      );

      // Processar apenas imagens que existem no Storage
      const urls = await Promise.all(
        result.items.map(async (itemRef) => {
          try {
            // Tentar obter URL com timeout
            const urlPromise = getDownloadURL(itemRef);
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 5000)
            );

            const url = await Promise.race([urlPromise, timeoutPromise]);

            // Adicionar múltiplos parâmetros anti-cache
            const timestamp = Date.now();
            const random = Math.random().toString(36).substring(7);
            const cacheBustingUrl = `${url}${
              url.includes("?") ? "&" : "?"
            }t=${timestamp}&nocache=${random}&v=${timestamp}`;

            const metadata = metadadosValidos.find(
              (m) => m.nomeArquivo === itemRef.name
            );

            // Validar se a URL é acessível
            return {
              url: cacheBustingUrl,
              titulo: metadata?.titulo || "",
              data: metadata?.data || "",
              descricao: metadata?.descricao || "",
              criadoEm: metadata?.criadoEm || new Date().toISOString(),
              nomeArquivo: itemRef.name,
            };
          } catch (error) {
            // Se falhar ao obter URL (imagem não existe ou timeout), pular esta imagem
            console.warn(`Erro ao carregar imagem ${itemRef.name}:`, error);
            return null;
          }
        })
      );

      // Filtrar imagens nulas e ordenar por data de criação (mais recentes primeiro)
      const imagensValidas = urls
        .filter((img) => img !== null)
        .sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));

      console.log(
        `[AreaVisitante] ${imagensValidas.length} imagens carregadas`
      );
      setImages(imagensValidas);
    } catch (error) {
      console.error("Error loading images:", error);
      setImages([]); // Garantir que o estado seja limpo em caso de erro
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array means this function is created once

  useEffect(() => {
    loadImages();

    // Recarregar imagens a cada 3 minutos para pegar novas imagens
    const reloadInterval = setInterval(() => {
      console.log("[AreaVisitante] Recarregando imagens automaticamente...");
      loadImages();
    }, 3 * 60 * 1000); // 3 minutos

    return () => clearInterval(reloadInterval);
  }, [loadImages]);

  // Carousel auto-play effect
  useEffect(() => {
    if (images.length > 1) {
      resetCarouselTimer();
    }

    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    };
  }, [images.length, resetCarouselTimer]);

  // Carousel navigation handlers - reinicia o timer ao trocar manualmente
  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    resetCarouselTimer();
  };

  const goToPreviousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    resetCarouselTimer();
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
    resetCarouselTimer();
  };

  // Image expansion handlers
  const openExpandedImage = (imageData) => {
    setExpandedImageUrl(imageData.url);
    document.body.style.overflow = "hidden";
  };

  const closeExpandedImage = () => {
    setExpandedImageUrl(null);
    document.body.style.overflow = "";
  };

  // Intersection Observer para animações de scroll
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    // Observar elementos com animação
    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  // Esconder header ao rolar para baixo, mostrar ao rolar para cima
  useEffect(() => {
    const container = visitorAreaRef.current;
    if (!container) return;

    const handleScroll = () => {
      const currentScrollY = container.scrollTop;
      if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
        setHeaderHidden(true);
      } else {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentScrollY;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll suave para seções com offset do header (visitor ou app)
  const scrollToSection = (sectionId) => {
    const container = visitorAreaRef.current;
    const section = document.getElementById(sectionId);
    if (!container || !section) return;

    // Visitor header (não logado) ou app header (logado)
    const visitorHeaderHeight = headerRef.current?.offsetHeight || 0;
    const appHeader = document.querySelector('header.card');
    const appHeaderHeight = appHeader?.offsetHeight || 0;
    const totalHeaderHeight = visitorHeaderHeight + appHeaderHeight;

    const containerRect = container.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const offset = sectionRect.top - containerRect.top + container.scrollTop - totalHeaderHeight;

    // Esconde o visitor header antes do scroll programático
    if (visitorHeaderHeight > 0) setHeaderHidden(true);

    container.scrollTo({ top: offset, behavior: 'smooth' });
  };

  const toggleConfissao = (index) => {
    setExpandedConfissao(prev => prev === index ? null : index);
  };

  const confissaoDeFe = [
    {
      titulo: "As Escrituras Sagradas",
      icone: "book-open",
      resumo: "Cremos na plena inspiração divina e na inerrância dos manuscritos originais das Escrituras Sagradas.",
      conteudo: [
        {
          subtitulo: "A Bíblia é plenamente inspirada e completa",
          texto: "Todos os 66 livros da Bíblia foram inspirados por Deus. Deus usou homens para escrevê-los, mas Ele soprou em seus ouvidos cada palavra; e ainda que as Escrituras tenham chegado até nós através da instrumentalidade humana, ela é a Palavra de Deus (2 Tm 3.16). As Escrituras não foram escritas por elucidação e vontade humana, mas estes falaram da parte de Deus, movidos pelo Espírito Santo (2 Pedro 1.20 e 21). Cremos que ela é a Palavra de Deus em toda a sua plenitude e que a Bíblia está completa e jamais haverá qualquer outro texto ou revelação que possa ser plenamente inspirada como ela."
        },
        {
          subtitulo: "A Bíblia é totalmente inerrante",
          texto: "Não existe na Bíblia qualquer erro ou inverdade, visto que ela foi inspirada por um Deus perfeito. Ela é pura em sua totalidade (Sl 12.6). Jamais alguma descoberta, algum fato novo, científico ou histórico, poderá contradizer a Bíblia. Cada fato verdadeiro é algo que Deus conhece desde a eternidade e que, portanto, não pode contradizer o que o Senhor fala nas Escrituras."
        },
        {
          subtitulo: "A Bíblia é a única regra de fé e prática",
          texto: "Toda a Escritura e tão somente a Escritura é a regra de fé e de prática para os salvos. Ela é proveitosa no seu todo para a salvação e para toda boa obra (2 Tm 3.14 a 17). Nenhum outro livro, ou dogma, ou revelação, tem a autoridade das Escrituras Sagradas. Os 66 livros são a autoridade final para a fé e a prática cristã (Dt 4.2; 12.32; Ap 22.18,19)."
        },
        {
          subtitulo: "A Bíblia é interpretada primeiramente por ela mesma",
          texto: "A Escritura deve ser interpretada primeiramente por ela mesma. Ela se explica, se completa, se relaciona, se exemplifica. A revelação é progressiva e na totalidade das Escrituras pode se ter uma melhor compreensão dela toda. A Bíblia é a fonte primária para a sua interpretação, as demais serão sempre consideradas como secundárias."
        }
      ],
      referencia: "Dt 4:2; Sl 119:112; Is 8:19,20; Dn 9:2; At 7:38; 2 Tm 3:16; Hb 1:1; 2 Pd 1:19-21; Ap 22:18,19 — Confissão de Fé, Artigo 5º"
    },
    {
      titulo: "A Trindade",
      icone: "triangle",
      resumo: "Cremos que há um só Deus na Sua essência, mas que subsiste em três pessoas distintas, coiguais em poder e em glória e coeternas.",
      conteudo: [
        {
          subtitulo: "A mesma essência divina nas três pessoas",
          texto: "O Pai, o Filho e o Espírito Santo são iguais em sua natureza. Toda a essência de Deus pertence igualmente a cada uma das três pessoas. A essência não é dividida entre as três pessoas, mas está com a totalidade absoluta da sua perfeição em cada uma delas. O Pai é Deus (Gn 1.1), o Filho é Deus (Jo 1.1-4; 20.27; Hb 1.3), e o Espírito Santo é Deus (Mt 28.19; At 5.3-4)."
        },
        {
          subtitulo: "Três pessoas distintas",
          texto: "Conquanto Deus seja um em sua essência, trata-se de três pessoas em sua natureza. O Pai não é o Filho, como se vê no batismo de Jesus (Mt 3.17). O Filho não é o Espírito, pois Jesus disse que enviaria o Espírito aos discípulos após a sua partida (Jo 15.16; 16.7). O Pai não é o Espírito, pois o Espírito Santo intercede por nós junto ao Pai (Rm 8.26)."
        },
        {
          subtitulo: "Iguais em poder, glória e eternidade",
          texto: "Todas as pessoas da Trindade são iguais em poder, glória e eternidade. Ambas estavam presentes na criação (Gn 1.26). A Trindade está presente em sua inteireza no batismo de Jesus (Mt 3.16,17). Nenhuma das pessoas teve início ou terá fim. Deus existe de eternidade a eternidade (Sl 90.2)."
        },
        {
          subtitulo: "Mesmos atributos e perfeições",
          texto: "Todas as pessoas da Trindade têm os mesmos atributos: são eternas, imutáveis, santas, onipotentes, onipresentes e oniscientes. Cada uma das pessoas é plenamente Deus. Deus Pai tem proeminência na criação, Deus Filho na obra salvífica do homem e o Deus Espírito Santo age primordialmente na santificação e preservação dos crentes."
        }
      ],
      referencia: "Mt 3:16,17; 28:18,19; Jo 14:16,17; 16:12-15; 2 Co 13:13; Gl 4:6,7; Hb 9:14; 1 Jo 2:22,23; 5:6-12 — Confissão de Fé, Artigo 1º"
    },
    {
      titulo: "Deus Pai",
      icone: "crown",
      resumo: "Cremos em Deus Pai, pessoal, espírito, eterno, infinito, imutável e insondável em seu ser, criador, preservador e consumador de todas as coisas.",
      conteudo: [
        {
          subtitulo: "Deus é pessoal e espiritual",
          texto: "Deus é um ser pessoal que se relaciona e se comunica com a sua criação, especialmente com os homens (Sl 40.1-3). Ele é um ser espiritual e não físico (Jo 4.24). Conforme 1 Timóteo 1.17, Ele é eterno, imortal e invisível. Existe de eternidade a eternidade (Sl 90.2), infinito em sabedoria e poder (Jr 10.10; Rm 1.20) e sabe de todas as coisas (Sl 139.7-12)."
        },
        {
          subtitulo: "Deus é imutável",
          texto: "Nele não pode existir variação ou sombra de mudança (Tg 1.17). A imutabilidade de Deus é a perfeição pela qual não haverá mudança não somente em seu ser, mas também em suas perfeições, em seus propósitos e em suas promessas."
        },
        {
          subtitulo: "Criador, preservador e consumador",
          texto: "Deus Pai é o principal agente na criação e preservação do mundo. Pela sua Palavra tudo veio a existir (Gn 1.1; Sl 33.6,9; At 17.24-26; Hb 11.3). Ele sustenta a criação com o seu poder. Nele vivemos, nos movemos e existimos (At 17.28). Ele cumprirá todos os seus propósitos e será responsável pelo surgimento do novo Céu e nova terra (2 Pe 3.13)."
        },
        {
          subtitulo: "Revelação de Deus",
          texto: "Deus se dá a conhecer ao homem pelas suas obras e especialmente pela sua Palavra. Toda a Escritura capacita o homem para o Céu e para toda boa obra (2 Tm 3.16). O Filho é a expressão exata do seu ser. Quem vê o Filho vê o Pai (Jo 12.45; 14.9-10)."
        }
      ],
      referencia: "Sl 9.2; 139.7-12; Is 40.28; Jr 10.10; Mt 5.45-48; Jo 1.18; 4:24; At 17.24-29; 1 Tm 1.17; Tg 1.17-18 — Confissão de Fé, Artigo 2º"
    },
    {
      titulo: "Deus Filho - Jesus Cristo",
      icone: "cross",
      resumo: "Cremos em Deus Filho, Jesus Cristo, o unigênito do Pai, concebido da Virgem Maria por obra e graça do Espírito Santo.",
      conteudo: [
        {
          subtitulo: "Contemplado no Antigo Testamento",
          texto: "A pré-existência de Jesus antes de sua encarnação está revelada nas Escrituras. Ele sempre existiu (Sl 2.1-8; Jo 8.56-58). Sua vinda física foi prevista em Isaías 7.14. O Senhor Jesus encarnado é a chave para a compreensão de todo o processo de Gênesis a Apocalipse."
        },
        {
          subtitulo: "O Filho é Unigênito",
          texto: "Jesus é o único gerado do Pai, da mesma essência que o Pai (Jo 1.14). Ele sempre foi da mesma natureza e teve os mesmos atributos que o Pai. Ele é um com o Pai (Jo 10.30). O Filho não é parte da criação; Ele é gerado, da mesma natureza do Pai."
        },
        {
          subtitulo: "Nasceu de uma virgem",
          texto: "Jesus veio à existência neste mundo de forma sobrenatural, sem a participação de José. A salvação vem do Senhor, usando a semente da mulher (Gn 3.15). O nascimento virginal possibilitou a união da plena divindade e da plena humanidade em uma só pessoa, conforme Isaías 7.14, cumprido em Mateus 1.18-21."
        },
        {
          subtitulo: "Viveu sem pecado",
          texto: "O Filho de Deus nunca pecou (Hb 4.14-16). Ainda que reconhecido em natureza humana, o Filho jamais pecou. Aquele que não conheceu pecado, Deus o fez pecado por nós (2 Co 5.21). Jesus não abdicou de sua natureza divina; Ele absorveu a humana sem deixar de ser Deus."
        },
        {
          subtitulo: "Morreu para expiar nossas culpas",
          texto: "Jesus foi entregue à morte por causa da nossa justificação (Rm 4.24). Ele se tornou o único mediador entre Deus e o homem (1 Tm 2.5-6). Jesus suportou a ira de Deus em favor da raça humana, entregando-se como um cordeiro por expiação do pecado dos homens (Jo 1.29)."
        },
        {
          subtitulo: "Ressuscitou para nossa justificação",
          texto: "A ressurreição de Jesus cumpre o seu papel vicário de nos garantir a justificação, completando sua obra redentora (Rm 4.25). Ele provou a maldição da Lei fazendo-se maldição em nosso lugar (Gl 3.13). Justificados mediante a fé, temos paz com Deus por meio de nosso Senhor Jesus Cristo (Rm 5.1)."
        },
        {
          subtitulo: "Ascendeu e intercede por nós",
          texto: "Jesus ressuscitou, passou quarenta dias na terra e subiu ao céu (At 1.3; Lc 24.50), assentando-se ao lado do Pai (At 7.55-56). Hoje Ele intercede como mediador a nosso favor. Ele é o nosso grande sumo sacerdote junto ao Pai, e podemos nos achegar a Ele com confiança (Hb 4.14-16)."
        },
        {
          subtitulo: "Voltará para julgar vivos e mortos",
          texto: "Jesus prometeu voltar (Jo 14.2-3) e julgará a todos, os vivos e os mortos (1 Pe 4.5). Neste julgamento Jesus cumprirá a sua promessa de punir os ímpios e galardoar os salvos. Mesmo os crentes serão julgados (Rm 14.10,12), mas absolvidos da condenação eterna (Rm 8.1), recebendo galardões segundo suas obras (2 Co 5.10)."
        }
      ],
      referencia: "Sl 2.1-8; Is 7.14; Mt 1.18-21; Jo 1.1-3; 8.56-58; 10.30; Rm 4.24,25; 1 Tm 2.5,6; Hb 4.14-16; 1 Pe 4.5; 1 Jo 4.8,9 — Confissão de Fé, Artigo 3º"
    },
    {
      titulo: "O Espírito Santo",
      icone: "wind",
      resumo: "Cremos em Deus Espírito Santo, da mesma essência do Pai e do Filho, regenerador, santificador, consolador das nossas vidas.",
      conteudo: [
        {
          subtitulo: "Regeneração do homem",
          texto: "O Espírito Santo atua na mente e coração do pecador, convencendo-o de seu estado de perdição e abrindo os seus olhos para a salvação em Cristo Jesus (Jo 3.6-7; 16.8-11; 2 Co 3.16-18). No momento da conversão acontece o batismo com o Espírito Santo, que passa a habitar no crente permanentemente (Jo 14.16,17; Rm 8.11)."
        },
        {
          subtitulo: "Santificação dos salvos",
          texto: "Na conversão o Espírito Santo passa a habitar no salvo e trabalha para que este seja cada vez mais santificado (Rm 8.4; 1 Co 6.11; 2 Ts 2.13). Ele atua para que o salvo produza o fruto do Espírito (Gl 5.22-23) e seja transformado, dia após dia, à imagem de Jesus (2 Co 3.18)."
        },
        {
          subtitulo: "O Consolador dos crentes",
          texto: "A palavra para Consolador, na língua original é parakletos, e significa aquele que está ao lado. O Espírito Santo continua a consolar os crentes após a assunção de Jesus ao Céu. Os discípulos não ficariam órfãos, mas teriam o Espírito Santo ao seu lado (Jo 14.16-18; 15.26-27)."
        },
        {
          subtitulo: "Introduz os homens no Corpo de Cristo",
          texto: "Ao habitar permanentemente no salvo, o Espírito Santo liga-o ao Corpo de Jesus, que é a igreja (Jo 14.16). Em um só Espírito todos somos batizados (1 Co 12.12-13). O Espírito Santo sela os crentes, guardando-os para a vida eterna (Ef 1.13)."
        },
        {
          subtitulo: "Concede dons espirituais",
          texto: "A ICEB crê que todos os dons são dados à igreja para a sua edificação, aperfeiçoamento e unidade (Rm 12.6-8; 1 Co 12.4-11; Ef 4.7-12; 1 Pe 4.10,11). Cremos que alguns dons são mais importantes que outros na edificação da igreja (1 Co 12.31). O critério para o uso dos dons na igreja é se ela está sendo edificada, aperfeiçoada e se mantendo unida."
        }
      ],
      referencia: "Sl 4.6; Jo 14.16,17; 16.7-14; 2 Co 3.16-18; Ef 2.17,18; 2 Ts 2.13; Tt 3.4,5; 1 Pe 1.3-12 — Confissão de Fé, Artigo 4º"
    },
    {
      titulo: "Salvação Eterna",
      icone: "shield-check",
      resumo: "Cremos na salvação eterna somente pela graça de Deus mediante a fé no sacrifício expiatório de Nosso Senhor Jesus Cristo.",
      conteudo: [
        {
          subtitulo: "Salvação pela graça mediante a fé",
          texto: "Salvação eterna significa que desde a sua conversão o homem já tem garantida a sua eterna salvação. Esta compreensão está de acordo com a teologia reformada que usa as expressões 'segurança eterna' ou 'segurança eterna dos crentes'. O salvo jamais perderá a salvação e nada e ninguém a tirará dele. Uma vez salvo, salvo para sempre."
        },
        {
          subtitulo: "A obra completa de Cristo",
          texto: "O sacrifício vicário de Cristo e a sua ressurreição já garantiram a nossa justificação (Rm 4.24-25). Se a obra de Cristo está completa — Ele já morreu pelos nossos pecados e já ressuscitou — da mesma forma já estamos justificados. Nenhuma condenação há para os que estão em Cristo Jesus (Rm 8.1). Um pecador é justificado não pelos seus próprios méritos, mas pelos de Cristo (2 Co 5.19-21)."
        },
        {
          subtitulo: "O selo do Espírito Santo",
          texto: "O Espírito Santo é a garantia de que somos de Jesus e de que seremos selados, guardados por este mesmo Espírito para a vida eterna (Ef 1.13). O Espírito Santo habita no salvo, santificando-o e conservando-o para a vida eterna (Jo 16.7-11)."
        },
        {
          subtitulo: "Propriedade de Deus",
          texto: "A expressão 'O Senhor conhece aqueles que lhe pertencem' (2 Tm 2.19) é uma garantia de propriedade. O salvo é conhecido por Deus e é propriedade de Deus (1 Pe 2.9). Conforme Jesus prometeu: 'Nenhum eu perca de todos os que me deu; pelo contrário, eu o ressuscitarei no último dia' (Jo 6.38-40)."
        }
      ],
      referencia: "Lc 19:10; Jo 16:7-11; At 4:12; Rm 4:24-25; 6:23; 2 Co 5:17; 2 Tm 2:19 — Confissão de Fé, Artigo 10º"
    },
    {
      titulo: "As Últimas Coisas",
      icone: "sunrise",
      resumo: "Cremos na segunda vinda de Cristo, pessoal, física e visível, para ressurreição dos mortos, arrebatamento da Igreja e julgamento das nações.",
      conteudo: [
        {
          subtitulo: "Jesus voltará pessoal, física e visivelmente",
          texto: "Ele aparecerá no céu, sobre as nuvens, visível a todos os olhos, com poder e glória, acompanhado de todos os seus anjos (Mt 24.30; 25.31; 26.64; Lc 21.27; At 1.11; 1 Ts 4.16). Os textos bíblicos não falam da volta de Jesus de forma simbólica ou virtual. Ele, pessoalmente, em seu estado de glória, virá à terra pela segunda vez."
        },
        {
          subtitulo: "Ressurreição e arrebatamento",
          texto: "Dos mortos ressuscitados uns irão para a vida eterna, outros para o inferno eterno (Dn 12.2). O Senhor descerá dos céus, os mortos em Cristo ressuscitarão primeiro; depois, nós, os vivos, seremos arrebatados juntamente com eles, entre nuvens, para o encontro do Senhor nos ares (1 Ts 4.16-17)."
        },
        {
          subtitulo: "Julgamento das nações",
          texto: "Haverá o julgamento do mundo inteiro, o juízo final (Mt 25.31-32; At 17.31; 1 Co 15.24-28). Todas as nações e todos os homens serão julgados, segundo as suas obras (2 Co 5.10; Ap 20.13). Haverá diferentes graus tanto de ventura no céu como de castigo eterno no inferno, determinados pelo que é feito enquanto na carne."
        },
        {
          subtitulo: "O Reino eterno e a consumação",
          texto: "Deus, no final de tudo, consumará todas as coisas e reinará para sempre. Haverá o estabelecimento do Reino de Deus, o novo céu e a nova terra (2 Pe 3.13). A nossa Confissão aponta finalmente para a consumação de todas as coisas com objetividade e clareza, à luz das Escrituras Sagradas."
        }
      ],
      referencia: "Dn 12.2; Mt 24.29-31; 25.31-32; Jo 14.1-3; At 1.9-11; 1 Ts 4.13-18; Ap 3.11; 20.1-13; 21.1-5 — Confissão de Fé, Artigos 14º e 15º"
    }
  ];

  return (
    <div
      ref={visitorAreaRef}
      className={`visitor-area ${isLoggedIn ? 'visitor-area--logged-in' : ''}`}
    >
      {/* Header próprio da área de visitantes - só aparece quando NÃO logado */}
      {!isLoggedIn && (
        <header ref={headerRef} className={`visitor-header ${headerHidden ? 'visitor-header--hidden' : ''}`}>
          <div className="visitor-header__content container">
            <div className="visitor-header__brand">
              <img
                src={logoExtendida}
                alt="ICES - Igreja Cristã Evangélica Siqueira"
                className="visitor-header__logo"
              />
            </div>

            <nav className="visitor-header__nav" aria-label="Navegação principal">
              <button
                className="nav-link"
                onClick={() => scrollToSection('carousel-section')}
              >
                Galeria
              </button>
              <button
                className="nav-link"
                onClick={() => scrollToSection('info-section')}
              >
                Horários
              </button>
              <button
                className="nav-link"
                onClick={() => scrollToSection('about-section')}
              >
                Sobre
              </button>
              <button
                className="nav-link"
                onClick={() => scrollToSection('confissao-section')}
              >
                Confissão de Fé
              </button>
            </nav>

            <button
              className="btn btn--primary btn--icon btn--glow"
              onClick={() => navigate("/login")}
              aria-label="Fazer Login"
            >
              <Icon name="login" size={16} />
              <span>Entrar</span>
            </button>
          </div>
        </header>
      )}

      <main className="visitor-main container">
        {/* Hero Welcome Section */}
        <section className="section welcome-section" id="hero-section">
          <FloatingParticles />

          <img
            src={logoExtendida}
            alt="ICES - Igreja Cristã Evangélica Siqueira"
            className="welcome-section__logo"
          />

          <h1 className="section__title welcome-section__title">
            Bem-vindo à nossa Casa
          </h1>

          <p className="section__description welcome-section__description">
            Um lugar de acolhimento, fé e esperança. Conheça nossa comunidade
            e descubra como podemos caminhar juntos.
          </p>

          <div className="welcome-section__cta">
            <button
              className="btn btn--primary btn--large btn--glow"
              onClick={() => scrollToSection('about-section')}
            >
              <Icon name="heart" size={18} />
              Nossa História
            </button>
            <button
              className="btn btn--outline-light btn--large"
              onClick={() => scrollToSection('carousel-section')}
            >
              <Icon name="image" size={18} />
              Galeria de Fotos
            </button>
          </div>

          <div className="welcome-section__scroll-indicator" onClick={() => scrollToSection('carousel-section')}>
            <span>Explore</span>
            <Icon name="chevron-down" size={18} />
          </div>
        </section>

        {/* Image Carousel Section */}
        <section
          id="carousel-section"
          className="section carousel-section"
          aria-label="Galeria de Imagens"
          data-animate
        >
          <h2 className="section__title carousel-section__title">
            <Icon name="image" size={24} />
            Nossa Galeria
          </h2>
          <div className="carousel">
            {isLoading ? (
              <div className="carousel__state carousel__state--loading">
                <span
                  className="spinner-border text-primary"
                  role="status"
                ></span>
                <p>Carregando imagens...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="carousel__state carousel__state--empty">
                <div
                  className="carousel__empty-icon"
                  role="img"
                  aria-label="Nenhuma imagem"
                >
                  🖼️
                </div>
                <p>Nenhuma imagem disponível no momento</p>
              </div>
            ) : (
              <>
                {images.map((imageData, index) => (
                  <div
                    key={`${imageData.nomeArquivo}-${index}`}
                    className={`carousel__image-container ${
                      index === currentImageIndex ? "is-active" : ""
                    }`}
                  >
                    <img
                      src={imageData.url}
                      alt={imageData.titulo || `Imagem ${index + 1}`}
                      className="carousel__image"
                      onClick={() => openExpandedImage(imageData)}
                      onError={(e) => {
                        console.warn(
                          `Erro ao carregar imagem: ${imageData.nomeArquivo}`
                        );
                        // Esconder imagem com erro
                        e.target.style.display = "none";
                        // Recarregar lista de imagens após pequeno delay
                        setTimeout(() => {
                          loadImages();
                        }, 1000);
                      }}
                      loading="eager"
                      aria-hidden={index !== currentImageIndex}
                    />

                    {/* Overlay com informações */}
                    {index === currentImageIndex &&
                      (imageData.titulo ||
                        imageData.data ||
                        imageData.descricao) && (
                        <div className="carousel__overlay">
                          <div className="carousel__overlay-content">
                            {imageData.titulo && (
                              <h3 className="carousel__overlay-title">
                                {imageData.titulo}
                              </h3>
                            )}
                            {imageData.data && (
                              <p className="carousel__overlay-date">
                                <Icon name="calendar" size={16} />
                                {(() => {
                                  const [ano, mes, dia] =
                                    imageData.data.split("-");
                                  const data = new Date(ano, mes - 1, dia);
                                  return data.toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  });
                                })()}
                              </p>
                            )}
                            {imageData.descricao && (
                              <p className="carousel__overlay-description">
                                {imageData.descricao}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                ))}

                {images.length > 1 && (
                  <>
                    <button
                      className="carousel__button carousel__button--prev"
                      onClick={goToPreviousImage}
                      aria-label="Imagem anterior"
                    >
                      ‹
                    </button>
                    <button
                      className="carousel__button carousel__button--next"
                      onClick={goToNextImage}
                      aria-label="Próxima imagem"
                    >
                      ›
                    </button>

                    <div className="carousel__indicators">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          className={`carousel__indicator ${
                            index === currentImageIndex ? "is-active" : ""
                          }`}
                          onClick={() => goToImage(index)}
                          aria-label={`Ir para imagem ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </section>

        {/* Info Cards */}
        <section id="info-section" className="info-cards-section" data-animate>
          <h2 className="section__title info-cards-section__title">
            <Icon name="info" size={24} />
            Informações
          </h2>

          <div className="info-cards">
            <article className="info-card" data-animate>
              <div className="info-card__icon-wrapper">
                <Icon name="calendar" size={28} />
              </div>
              <div className="info-card__header">
                <h3 className="info-card__title">Horários de Culto</h3>
              </div>
              <div className="info-card__body">
                <div className="schedule-item">
                  <span className="schedule-item__day">Quinta-feira</span>
                  <span className="schedule-item__time">19h30</span>
                  <span className="schedule-item__desc">Escola Bíblica e Oração</span>
                </div>
                <div className="schedule-item">
                  <span className="schedule-item__day">Domingo</span>
                  <span className="schedule-item__time">17h00</span>
                  <span className="schedule-item__desc">Culto de Louvor e Adoração</span>
                </div>
              </div>
            </article>

            <article className="info-card" data-animate>
              <div className="info-card__icon-wrapper">
                <Icon name="map-pin" size={28} />
              </div>
              <div className="info-card__header">
                <h3 className="info-card__title">Localização</h3>
              </div>
              <div className="info-card__body">
                <address className="info-card__address">
                  <strong>Endereço:</strong><br />
                  Rua Mário Monte Negro, 188<br />
                  Siqueira - Fortaleza/CE
                </address>

                <div className="location-actions">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=-3.813893,-38.613436"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--outline btn--small btn--pill"
                  >
                    <Icon name="map-pin" size={14} />
                    Google Maps
                  </a>
                  <a
                    href="https://waze.com/ul?ll=-3.813893,-38.613436&navigate=yes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--outline btn--small btn--pill"
                  >
                    <Icon name="navigation" size={14} />
                    Waze
                  </a>
                  <a
                    href="https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[latitude]=-3.813893&dropoff[longitude]=-38.613436"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--outline btn--small btn--pill"
                  >
                    <Icon name="car" size={14} />
                    Uber
                  </a>
                </div>
              </div>
            </article>

            <article className="info-card" data-animate>
              <div className="info-card__icon-wrapper">
                <Icon name="user" size={28} />
              </div>
              <div className="info-card__header">
                <h3 className="info-card__title">Liderança</h3>
              </div>
              <div className="info-card__body">
                <div className="leader-info">
                  <strong>Pastor:</strong> Jucileu Segundo<br />
                  <strong>Telefone:</strong> (85) 98623-0863
                </div>
                <div className="social-links">
                  <a
                    href="https://wa.me/5585986230863"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link social-link--whatsapp"
                    aria-label="WhatsApp"
                  >
                    <img
                      src={whatsapp}
                      alt="WhatsApp"
                      className="social-link__icon"
                    />
                    <span>WhatsApp</span>
                  </a>
                  <a
                    href="https://www.instagram.com/icesiqueiraice/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link social-link--instagram"
                    aria-label="Instagram"
                  >
                    <img
                      src={instagram}
                      alt="Instagram"
                      className="social-link__icon"
                    />
                    <span>Instagram</span>
                  </a>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* About Our Church Section */}
        <section id="about-section" className="section about-section" data-animate>
          <div className="about-section__header">
            <h2 className="section__title about-section__title">
              <Icon name="book-open" size={28} />
              Sobre Nossa Igreja
            </h2>
            <p className="about-section__lema">
              "Salvos pela graça, vivendo para Sua glória."
            </p>
          </div>

          <div className="about-section__content">
            <div className="about-mvv-grid">
              <div className="mvv-card">
                <div className="mvv-card__icon">
                  <Icon name="target" size={28} />
                </div>
                <h3 className="mvv-card__label">Missão</h3>
                <p className="mvv-card__subtitle">O que fazemos todos os dias</p>
                <p className="mvv-card__text">
                  "Glorificar a Deus através da proclamação fiel do Evangelho da
                  graça, servindo ao próximo em amor e santidade, e fazendo
                  discípulos que vivam para o louvor da Sua glória."
                </p>
              </div>

              <div className="mvv-card">
                <div className="mvv-card__icon">
                  <Icon name="eye" size={28} />
                </div>
                <h3 className="mvv-card__label">Visão</h3>
                <p className="mvv-card__subtitle">Onde queremos chegar</p>
                <p className="mvv-card__text">
                  "Ser uma comunidade de fé biblicamente reformada que reflete a
                  luz de Cristo, transformando vidas e a sociedade pelo poder do
                  Espírito Santo, até que sejamos plenamente conformados à imagem
                  do Filho."
                </p>
              </div>
            </div>

            <div className="about-section__values">
              <h3 className="values-title">
                <Icon name="award" size={24} />
                Nossos Valores
              </h3>
              <p className="values-subtitle">Os princípios inegociáveis</p>
              <div className="values-grid">
                <div className="value-item">
                  <div className="value-item__icon">
                    <Icon name="crown" size={22} />
                  </div>
                  <h4 className="value-item__title">Soberania e Glória de Deus</h4>
                  <p className="value-item__text">Tudo o que fazemos é por Ele e para Ele.</p>
                </div>
                <div className="value-item">
                  <div className="value-item__icon">
                    <Icon name="gift" size={22} />
                  </div>
                  <h4 className="value-item__title">Graça Incondicional</h4>
                  <p className="value-item__text">Reconhecemos que nossa salvação é um presente imerecido, o que nos move à humildade e gratidão.</p>
                </div>
                <div className="value-item">
                  <div className="value-item__icon">
                    <Icon name="book" size={22} />
                  </div>
                  <h4 className="value-item__title">Fidelidade às Escrituras</h4>
                  <p className="value-item__text">Nosso compromisso é com a verdade revelada, interpretada à luz da tradição reformada.</p>
                </div>
                <div className="value-item">
                  <div className="value-item__icon">
                    <Icon name="star" size={22} />
                  </div>
                  <h4 className="value-item__title">Santidade e Caráter de Cristo</h4>
                  <p className="value-item__text">Buscamos a pureza de vida, desejando que nosso viver seja o reflexo de quem Cristo é.</p>
                </div>
                <div className="value-item">
                  <div className="value-item__icon">
                    <Icon name="heart" size={22} />
                  </div>
                  <h4 className="value-item__title">Amor Servil</h4>
                  <p className="value-item__text">Seguimos o exemplo de Cristo, que não veio para ser servido, mas para servir e dar a vida por muitos.</p>
                </div>
                <div className="value-item">
                  <div className="value-item__icon">
                    <Icon name="send" size={22} />
                  </div>
                  <h4 className="value-item__title">Evangelismo e Missão</h4>
                  <p className="value-item__text">O compromisso de levar a mensagem da graça a todas as pessoas, sendo luz em um mundo de trevas.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Confissão de Fé Section */}
        <section id="confissao-section" className="section confissao-section" data-animate>
          <div className="confissao-section__header">
            <h2 className="section__title confissao-section__title">
              <Icon name="scroll" size={28} />
              Confissão de Fé
            </h2>
            <p className="section__description">
              Conheça os fundamentos doutrinários da Igreja Cristã Evangélica do Brasil (ICEB),
              baseados nas Escrituras Sagradas.
            </p>
          </div>

          <div className="confissao-accordion">
            {confissaoDeFe.map((item, index) => (
              <div
                key={index}
                className={`confissao-item ${expandedConfissao === index ? 'confissao-item--expanded' : ''}`}
              >
                <button
                  className="confissao-item__header"
                  onClick={() => toggleConfissao(index)}
                  aria-expanded={expandedConfissao === index}
                  aria-controls={`confissao-content-${index}`}
                >
                  <div className="confissao-item__icon">
                    <Icon name={item.icone} size={22} />
                  </div>
                  <div className="confissao-item__header-text">
                    <h3 className="confissao-item__title">{item.titulo}</h3>
                    <p className="confissao-item__resumo">{item.resumo}</p>
                  </div>
                  <div className="confissao-item__chevron">
                    <Icon name="chevron-down" size={20} />
                  </div>
                </button>

                <div
                  id={`confissao-content-${index}`}
                  className="confissao-item__content"
                  role="region"
                  aria-labelledby={`confissao-header-${index}`}
                >
                  <div className="confissao-item__body">
                    {item.conteudo.map((sub, subIndex) => (
                      <div key={subIndex} className="confissao-sub">
                        <h4 className="confissao-sub__title">
                          <span className="confissao-sub__number">{subIndex + 1}</span>
                          {sub.subtitulo}
                        </h4>
                        <p className="confissao-sub__text">{sub.texto}</p>
                      </div>
                    ))}
                    <div className="confissao-item__referencia">
                      <Icon name="bookmark" size={14} />
                      <span>{item.referencia}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Restricted Area Card */}
        <section className="section restricted-area-card" data-animate>
          <div className="restricted-area-card__glow" aria-hidden="true" />

          <div className="restricted-area-card__icon">
            <Icon name="shield" size={40} />
          </div>

          <h2 className="restricted-area-card__title">
            <Icon name="lock" size={24} />
            Área Restrita - Membros
          </h2>

          <p className="restricted-area-card__description">
            Se você é membro, congregado ou frequentador da igreja e deseja
            acessar o sistema interno, faça login com suas credenciais.
          </p>

          <div className="restricted-area-card__info-box">
            <div className="info-box__header">
              <Icon name="info" size={18} />
              <strong>Para ter acesso ao sistema:</strong>
            </div>
            <ol className="info-box__list">
              <li>
                <span className="step-number">1</span>
                <span>Você deve estar cadastrado como membro na igreja</span>
              </li>
              <li>
                <span className="step-number">2</span>
                <span>Solicite ao secretário um link de convite por e-mail</span>
              </li>
              <li>
                <span className="step-number">3</span>
                <span>Ou peça para incluir seu e-mail Google no cadastro</span>
              </li>
              <li>
                <span className="step-number">4</span>
                <span>Faça login com sua conta Google</span>
              </li>
            </ol>
          </div>

          <button
            className="btn btn--primary btn--large btn--glow"
            onClick={() => navigate("/login")}
            aria-label="Acessar Sistema de Membros"
          >
            <Icon name="login" size={20} />
            <span>Acessar Sistema de Membros</span>
          </button>
        </section>
      </main>

      <footer className="visitor-footer">
        <div className="visitor-footer__content container">
          <div className="visitor-footer__brand">
            <img
              src={logoExtendida}
              alt="ICES"
              className="visitor-footer__logo"
            />
          </div>

          <div className="visitor-footer__links">
            <a
              href="https://wa.me/5585986230863"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              <Icon name="phone" size={16} />
              Contato
            </a>
            <button
              className="footer-link"
              onClick={() => scrollToSection('info-section')}
            >
              <Icon name="map-pin" size={16} />
              Localização
            </button>
            <button
              className="footer-link"
              onClick={() => scrollToSection('about-section')}
            >
              <Icon name="info" size={16} />
              Sobre
            </button>
            <button
              className="footer-link"
              onClick={() => scrollToSection('confissao-section')}
            >
              <Icon name="scroll" size={16} />
              Confissão de Fé
            </button>
          </div>

          <div className="visitor-footer__social">
            <a
              href="https://wa.me/5585986230863"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="WhatsApp"
            >
              <img src={whatsapp} alt="WhatsApp" />
            </a>
            <a
              href="https://www.instagram.com/icesiqueiraice/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="Instagram"
            >
              <img src={instagram} alt="Instagram" />
            </a>
          </div>
        </div>

        <div className="visitor-footer__bottom">
          <p className="visitor-footer__text">
            &copy; {new Date().getFullYear()} ICES - Igreja Cristã Evangélica Siqueira
          </p>
          <p className="visitor-footer__subtitle">
            Todos os direitos reservados
          </p>
        </div>
      </footer>

      {/* Expanded Image Modal */}
      {expandedImageUrl && (
        <div className="image-modal-overlay" onClick={closeExpandedImage}>
          <div className="image-modal-content">
            <button
              className="image-modal-close"
              onClick={closeExpandedImage}
              aria-label="Fechar imagem"
            >
              <Icon name="x" size={24} />
            </button>
            <img
              src={expandedImageUrl}
              alt="Imagem expandida"
              className="image-modal-img"
              loading="eager"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image itself
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaVisitante;
